import React, { useEffect, useState, useRef, lazy } from 'react';
import { MapPinIcon, PhoneIcon, MailIcon, ClockIcon, AlertCircleIcon, CheckCircleIcon } from 'lucide-react';
import { SEO } from '../components/SEO';
export const ContactPage = () => {
  // Generate random math challenge
  const generateMathChallenge = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    return {
      question: `${num1} + ${num2}`,
      answer: num1 + num2
    };
  };
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'informatie',
    message: '',
    honeypot: '',
    captchaAnswer: ''
  });
  // Captcha state
  const [captcha, setCaptcha] = useState(generateMathChallenge());
  // Form status state
  const [formStatus, setFormStatus] = useState({
    isSubmitting: false,
    isSubmitted: false,
    isError: false,
    message: '',
    attempts: 0,
    lastSubmitTime: 0
  });
  // Timestamp for form rendering (anti-bot measure)
  const [formRenderedAt] = useState(Date.now());
  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Generate HTML email template
  const generateEmailHTML = (data: typeof formData) => {
    const subjectLabels: {
      [key: string]: string;
    } = {
      informatie: 'Informatie aanvragen',
      afspraak: 'Afspraak maken',
      offerte: 'Offerte aanvragen',
      anders: 'Anders'
    };
    return `
<!DOCTYPE html>
<html lang="nl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Contactformulier - BKCustoms</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
  <table role="presentation" style="width: 100%; border-collapse: collapse; background-color: #f5f5f5;">
    <tr>
      <td style="padding: 40px 20px;">
        <table role="presentation" style="width: 100%; max-width: 600px; margin: 0 auto; background-color: #ffffff; border-collapse: collapse; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          <!-- Header -->
          <tr>
            <td style="padding: 40px 40px 30px 40px; text-align: center; border-bottom: 3px solid #D6281F;">
              <h1 style="margin: 0; font-size: 32px; font-weight: 700; color: #0a0a0a; letter-spacing: -0.5px;">
                BKCustoms
              </h1>
              <p style="margin: 8px 0 0 0; font-size: 16px; color: #666666; font-weight: 400;">
                Contactformulier inzending
              </p>
            </td>
          </tr>
          <!-- Content -->
          <tr>
            <td style="padding: 40px;">
              <table role="presentation" style="width: 100%; border-collapse: collapse;">
                <tr>
                  <td style="padding-bottom: 24px;">
                    <p style="margin: 0; font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: #999999; font-weight: 600;">Naam</p>
                    <p style="margin: 4px 0 0 0; font-size: 16px; color: #0a0a0a;">${data.name}</p>
                  </td>
                </tr>
                <tr>
                  <td style="padding-bottom: 24px;">
                    <p style="margin: 0; font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: #999999; font-weight: 600;">E-mail</p>
                    <p style="margin: 4px 0 0 0; font-size: 16px;">
                      <a href="mailto:${data.email}" style="color: #D6281F; text-decoration: none;">${data.email}</a>
                    </p>
                  </td>
                </tr>
                ${data.phone ? `
                <tr>
                  <td style="padding-bottom: 24px;">
                    <p style="margin: 0; font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: #999999; font-weight: 600;">Telefoonnummer</p>
                    <p style="margin: 4px 0 0 0; font-size: 16px;">
                      <a href="tel:${data.phone}" style="color: #D6281F; text-decoration: none;">${data.phone}</a>
                    </p>
                  </td>
                </tr>
                ` : ''}
                <tr>
                  <td style="padding-bottom: 24px;">
                    <p style="margin: 0; font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: #999999; font-weight: 600;">Onderwerp</p>
                    <p style="margin: 4px 0 0 0; font-size: 16px; color: #0a0a0a;">${subjectLabels[data.subject] || data.subject}</p>
                  </td>
                </tr>
                <tr>
                  <td style="padding-bottom: 24px;">
                    <p style="margin: 0; font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: #999999; font-weight: 600;">Bericht</p>
                    <p style="margin: 4px 0 0 0; font-size: 16px; color: #0a0a0a; line-height: 1.6; white-space: pre-wrap;">${data.message}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- Footer -->
          <tr>
            <td style="padding: 30px 40px; background-color: #f9f9f9; border-top: 1px solid #e5e5e5;">
              <p style="margin: 0 0 16px 0; font-size: 12px; color: #999999; line-height: 1.5;">
                Verzonden op ${new Date().toLocaleDateString('nl-NL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })} om ${new Date().toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit'
    })}
              </p>
              <p style="margin: 0; font-size: 12px; color: #666666; line-height: 1.5;">
                <strong style="color: #0a0a0a;">BKCustoms Car Styling</strong><br>
                Microfoonstraat 36, 1322 BN Almere<br>
                <a href="tel:+31627888306" style="color: #D6281F; text-decoration: none;">+31 (0)6 278 88 306</a> • 
                <a href="mailto:info@bkcustoms.nl" style="color: #D6281F; text-decoration: none;">info@bkcustoms.nl</a><br>
                <a href="https://www.bkcustoms.nl" style="color: #D6281F; text-decoration: none;">www.bkcustoms.nl</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
    `.trim();
  };
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Check if form was filled out too quickly (likely a bot)
    const timeElapsed = Date.now() - formRenderedAt;
    if (timeElapsed < 3000) {
      setFormStatus({
        ...formStatus,
        isError: true,
        message: 'Formulier te snel ingevuld. Probeer het opnieuw.'
      });
      return;
    }
    // Check if honeypot field was filled (bot trap)
    if (formData.honeypot) {
      // Silently reject the submission but pretend it was successful
      setFormStatus({
        ...formStatus,
        isSubmitting: false,
        isSubmitted: true,
        message: 'Bedankt voor uw bericht. We nemen zo spoedig mogelijk contact met u op.'
      });
      return;
    }
    // Verify CAPTCHA
    const userAnswer = parseInt(formData.captchaAnswer);
    if (isNaN(userAnswer) || userAnswer !== captcha.answer) {
      setFormStatus({
        ...formStatus,
        isError: true,
        message: 'Onjuist antwoord op de verificatievraag. Probeer het opnieuw.'
      });
      // Generate new challenge
      setCaptcha(generateMathChallenge());
      setFormData(prev => ({
        ...prev,
        captchaAnswer: ''
      }));
      return;
    }
    // Rate limiting check
    const now = Date.now();
    const timeSinceLastSubmit = now - formStatus.lastSubmitTime;
    if (formStatus.attempts >= 3 && timeSinceLastSubmit < 60000) {
      setFormStatus({
        ...formStatus,
        isError: true,
        message: 'Te veel pogingen. Probeer het over een minuut opnieuw.'
      });
      return;
    }
    // Basic form validation
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus({
        ...formStatus,
        isError: true,
        message: 'Vul alle verplichte velden in.'
      });
      return;
    }
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormStatus({
        ...formStatus,
        isError: true,
        message: 'Vul een geldig e-mailadres in.'
      });
      return;
    }
    // Set submitting state
    setFormStatus({
      ...formStatus,
      isSubmitting: true,
      isError: false,
      message: ''
    });
    try {
      // Generate dynamic subject line
      const subjectLabels: {
        [key: string]: string;
      } = {
        informatie: 'Informatie aanvragen',
        afspraak: 'Afspraak maken',
        offerte: 'Offerte aanvragen',
        anders: 'Anders'
      };
      const dynamicSubject = `Nieuw bericht: ${subjectLabels[formData.subject] || formData.subject} - ${formData.name}`;
      // Prepare webhook payload
      const webhookPayload = {
        subject: dynamicSubject,
        sender: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Niet opgegeven'
        },
        formFields: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Niet opgegeven',
          subject: formData.subject,
          message: formData.message
        },
        htmlEmail: generateEmailHTML(formData),
        submittedAt: new Date().toISOString(),
        timestamp: now
      };
      // Send to webhook
      const response = await fetch('https://hook.eu2.make.com/e8n7hb0m6murkud23ghvivzfho5r1qyu', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(webhookPayload)
      });
      if (!response.ok) {
        throw new Error('Webhook request failed');
      }
      // Reset form and update status
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: 'informatie',
        message: '',
        honeypot: '',
        captchaAnswer: ''
      });
      // Generate new captcha for next submission
      setCaptcha(generateMathChallenge());
      setFormStatus({
        isSubmitting: false,
        isSubmitted: true,
        isError: false,
        message: 'Bedankt voor uw bericht. We nemen zo spoedig mogelijk contact met u op.',
        attempts: formStatus.attempts + 1,
        lastSubmitTime: now
      });
    } catch (error) {
      setFormStatus({
        ...formStatus,
        isSubmitting: false,
        isError: true,
        message: 'Er is iets misgegaan. Probeer het later opnieuw.',
        attempts: formStatus.attempts + 1,
        lastSubmitTime: now
      });
    }
  };
  return <main data-id="element-599">
      <SEO title="Contact | BKCustoms Car Styling" description="Heeft u vragen of wilt u een afspraak maken? Neem contact met ons op via ons contactformulier, telefoon of e-mail." data-id="element-600" />
      {/* Header Banner */}
      <div className="relative bg-[#0a0a0a] py-20" data-id="element-601">
        <div className="absolute inset-0 overflow-hidden" data-id="element-602">
          <div className="absolute inset-0 bg-black/80 z-10" data-id="element-603"></div>
          <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Contact BKCustoms" className="w-full h-full object-cover object-center" data-id="element-604" />
        </div>
        <div className="relative z-20 max-w-7xl mx-auto px-6 lg:px-8 text-center" data-id="element-605">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-606">
            Contact <span className="text-[#D6281F]" data-id="element-607">Opnemen</span>
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto text-lg" data-id="element-608">
            Heeft u vragen of wilt u een afspraak maken? Neem contact met ons
            op.
          </p>
        </div>
      </div>
      {/* Contact Section */}
      <section className="bg-[#0a0a0a] py-20" data-id="element-609">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-610">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-611">
            {/* Contact Form */}
            <div data-id="element-612">
              <div className="bg-[#0f0f0f] border border-[#222] rounded-lg p-8" data-id="element-613">
                <h2 className="text-2xl font-bold text-white mb-6" data-id="element-614">
                  Stuur ons een bericht
                </h2>
                {formStatus.isSubmitted ? <div className="bg-green-500/10 border border-green-500/30 rounded-md p-4 mb-6 flex items-start" data-id="element-615">
                    <CheckCircleIcon className="text-green-500 mr-3 mt-0.5 flex-shrink-0" size={18} data-id="element-616" />
                    <p className="text-white/90" data-id="element-617">{formStatus.message}</p>
                  </div> : formStatus.isError ? <div className="bg-red-500/10 border border-red-500/30 rounded-md p-4 mb-6 flex items-start" data-id="element-618">
                    <AlertCircleIcon className="text-red-500 mr-3 mt-0.5 flex-shrink-0" size={18} data-id="element-619" />
                    <p className="text-white/90" data-id="element-620">{formStatus.message}</p>
                  </div> : null}
                <form onSubmit={handleSubmit} className="contact-form" data-id="element-621">
                  <input type="hidden" name="formRenderedAt" value={formRenderedAt} data-id="element-622" />
                  <div className="hidden-field" aria-hidden="true" style={{
                  display: 'none'
                }} data-id="element-623">
                    <input type="text" name="honeypot" id="contact-check" autoComplete="off" value={formData.honeypot} onChange={handleChange} tabIndex={-1} data-id="element-624" />
                  </div>
                  <div className="mb-6" data-id="element-625">
                    <label htmlFor="contact-name" className="block text-white/80 mb-2 text-sm" data-id="element-626">
                      Naam <span className="text-[#D6281F]" data-id="element-627">*</span>
                    </label>
                    <input type="text" id="contact-name" name="name" value={formData.name} onChange={handleChange} className="w-full bg-[#0a0a0a] border border-[#333] rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#D6281F]" required data-id="element-628" />
                  </div>
                  <div className="mb-6" data-id="element-629">
                    <label htmlFor="contact-email" className="block text-white/80 mb-2 text-sm" data-id="element-630">
                      E-mail <span className="text-[#D6281F]" data-id="element-631">*</span>
                    </label>
                    <input type="email" id="contact-email" name="email" value={formData.email} onChange={handleChange} className="w-full bg-[#0a0a0a] border border-[#333] rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#D6281F]" required data-id="element-632" />
                  </div>
                  <div className="mb-6" data-id="element-633">
                    <label htmlFor="contact-phone" className="block text-white/80 mb-2 text-sm" data-id="element-634">
                      Telefoonnummer
                    </label>
                    <input type="tel" id="contact-phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full bg-[#0a0a0a] border border-[#333] rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#D6281F]" data-id="element-635" />
                  </div>
                  <div className="mb-6" data-id="element-636">
                    <label htmlFor="contact-subject" className="block text-white/80 mb-2 text-sm" data-id="element-637">
                      Onderwerp
                    </label>
                    <select id="contact-subject" name="subject" value={formData.subject} onChange={handleChange} className="w-full bg-[#0a0a0a] border border-[#333] rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#D6281F]" data-id="element-638">
                      <option value="informatie" data-id="element-639">Informatie aanvragen</option>
                      <option value="afspraak" data-id="element-640">Afspraak maken</option>
                      <option value="offerte" data-id="element-641">Offerte aanvragen</option>
                      <option value="anders" data-id="element-642">Anders</option>
                    </select>
                  </div>
                  <div className="mb-6" data-id="element-643">
                    <label htmlFor="contact-message" className="block text-white/80 mb-2 text-sm" data-id="element-644">
                      Bericht <span className="text-[#D6281F]" data-id="element-645">*</span>
                    </label>
                    <textarea id="contact-message" name="message" value={formData.message} onChange={handleChange} rows={5} className="w-full bg-[#0a0a0a] border border-[#333] rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#D6281F]" required data-id="element-646"></textarea>
                  </div>
                  {/* Simple CAPTCHA */}
                  <div className="mb-6" data-id="element-647">
                    <label htmlFor="contact-captcha" className="block text-white/80 mb-2 text-sm" data-id="element-648">
                      Verificatie: Hoeveel is {captcha.question}?{' '}
                      <span className="text-[#D6281F]" data-id="element-649">*</span>
                    </label>
                    <input type="text" id="contact-captcha" name="captchaAnswer" value={formData.captchaAnswer} onChange={handleChange} className="w-full bg-[#0a0a0a] border border-[#333] rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#D6281F]" placeholder="Vul het antwoord in" required autoComplete="off" data-id="element-650" />
                  </div>
                  <div className="mb-6 p-3 bg-[#0a0a0a] border border-[#333] rounded-md" data-id="element-651">
                    <p className="text-white/70 text-sm" data-id="element-652">
                      Om spam te voorkomen, controleren wij handmatig alle
                      berichten. Vul alstublieft alle verplichte velden in.
                    </p>
                  </div>
                  <button type="submit" disabled={formStatus.isSubmitting} className={`w-full bg-[#D6281F] hover:bg-[#D6281F]/90 text-white py-3 rounded-md transition-all duration-300 font-medium ${formStatus.isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-653">
                    {formStatus.isSubmitting ? 'Bezig met versturen...' : 'Versturen'}
                  </button>
                  <p className="text-white/50 text-xs mt-4" data-id="element-654">
                    Door dit formulier te versturen gaat u akkoord met ons{' '}
                    <a href="/privacybeleid" className="text-[#D6281F] hover:underline" data-id="element-655">
                      privacybeleid
                    </a>
                    .
                  </p>
                </form>
              </div>
            </div>
            {/* Contact Info & Map */}
            <div data-id="element-656">
              <div className="bg-[#0f0f0f] border border-[#222] rounded-lg p-8 mb-8" data-id="element-657">
                <h2 className="text-2xl font-bold text-white mb-6" data-id="element-658">
                  Contactgegevens
                </h2>
                <div className="space-y-6" data-id="element-659">
                  <div className="flex items-start" data-id="element-660">
                    <MapPinIcon size={24} className="text-[#D6281F] mr-4 mt-1 flex-shrink-0" data-id="element-661" />
                    <div data-id="element-662">
                      <h3 className="text-white font-medium mb-1" data-id="element-663">Adres</h3>
                      <p className="text-white/80" data-id="element-664">
                        Microfoonstraat 36
                        <br data-id="element-665" />
                        1322 BN Almere
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-666">
                    <PhoneIcon size={24} className="text-[#D6281F] mr-4 mt-1 flex-shrink-0" data-id="element-667" />
                    <div data-id="element-668">
                      <h3 className="text-white font-medium mb-1" data-id="element-669">Telefoon</h3>
                      <p className="text-white/80" data-id="element-670">
                        <a href="tel:+31627888306" className="hover:text-white transition-colors" data-id="element-671">
                          +31 (0)6 278 88 306
                        </a>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-672">
                    <MailIcon size={24} className="text-[#D6281F] mr-4 mt-1 flex-shrink-0" data-id="element-673" />
                    <div data-id="element-674">
                      <h3 className="text-white font-medium mb-1" data-id="element-675">E-mail</h3>
                      <p className="text-white/80" data-id="element-676">
                        <a href="mailto:info@bkcustoms.nl" className="hover:text-white transition-colors" data-id="element-677">
                          info@bkcustoms.nl
                        </a>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-678">
                    <ClockIcon size={24} className="text-[#D6281F] mr-4 mt-1 flex-shrink-0" data-id="element-679" />
                    <div data-id="element-680">
                      <h3 className="text-white font-medium mb-1" data-id="element-681">
                        Openingstijden
                      </h3>
                      <p className="text-white/80" data-id="element-682">
                        Maandag - Zaterdag: 08:00 - 19:30
                        <br data-id="element-683" />
                        Zondag: Op afspraak
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Map */}
              <div className="bg-[#0f0f0f] border border-[#222] rounded-lg overflow-hidden h-80" data-id="element-684">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2435.4380679053694!2d5.221333776885494!3d52.37541904430272!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c617b0c02a7c11%3A0x40c8a23d1e7a2f5d!2sMicrofoonstraat%2036%2C%201322%20BN%20Almere!5e0!3m2!1sen!2snl!4v1685889643728!5m2!1sen!2snl" width="100%" height="100%" style={{
                border: 0
              }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="BKCustoms locatie" data-id="element-685"></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
};